import { Engine, Policy } from '@agilelab/plugin-wb-governance-common';
import {
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
} from '@agilelab/plugin-wb-platform';
import { Grid, useMediaQuery } from '@material-ui/core';
import { FC, useMemo, useState } from 'react';
import { extractGovernanceErrorInfo } from '../../../../utils/governance.utils';
import { Editor } from '../../../common/Editor/Editor';
import { ErrorBox } from '../../../common/ErrorBox/ErrorBox';
import { EditorInfo } from '../../../common/Editor/EditorInfo';

interface Props {
  policy: Policy;
  test: {
    errors?: string[];
    content: string;
    name: string;
  };
}

export const GovernanceTestOverviewPolicyDetails: FC<Props> = props => {
  const { test, policy } = props;
  const [currentErrorIndex, setCurrentErrorIndex] = useState<number>(0);
  const [isPrimaryCollapsed, setIsPrimaryCollapsed] = useState(false);
  const isMobile = useMediaQuery('(max-width:960px)');

  const hasTwoPanels = useMemo(
    () =>
      Boolean(
        (policy.engine === Engine.Cue ||
          policy.engine === Engine.GovernanceAgent) &&
          test.content,
      ),
    [policy.engine, test.content],
  );

  const focusEditorLine = (line: number, id: string) => {
    const editorElement = document.getElementById(id);
    const monacoEditor = (editorElement as any).monacoEditor;

    if (monacoEditor) {
      monacoEditor.revealLineInCenter(line);
      monacoEditor.setPosition({ lineNumber: line, column: 1 });
      monacoEditor.focus();
    }
  };

  const handleGoToLine = (line: number, id: string) => {
    const isPolicyEditor = id.startsWith('policy-');
    const isDescriptorEditor = id.startsWith('descriptor-');
    let targetCollapsedState = isPrimaryCollapsed;

    if (isPolicyEditor) {
      targetCollapsedState = false;
    } else if (isDescriptorEditor) {
      targetCollapsedState = isMobile;
    }

    if (hasTwoPanels && targetCollapsedState !== isPrimaryCollapsed) {
      setIsPrimaryCollapsed(targetCollapsedState);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          focusEditorLine(line, id);
        });
      });
      return;
    }

    focusEditorLine(line, id);
  };
  const errors = useMemo(
    () =>
      (test?.errors || []).map(error =>
        extractGovernanceErrorInfo({ policyId: policy.policyId, error }),
      ),
    [policy.policyId, test?.errors],
  );

  const currentError = useMemo(
    () => errors?.at(currentErrorIndex),
    [currentErrorIndex, errors],
  );

  const handleSwitch = (index: number) => {
    setCurrentErrorIndex(index);
  };

  return (
    <Grid container style={{ flex: 1 }}>
      {!!currentError && (
        <Grid item xs={12}>
          <ErrorBox
            policyEditorId={`policy-${props.policy.groupId}`}
            descriptorEditorId={`descriptor-${props.policy.groupId}`}
            headerTitle="Error Info"
            error={currentError}
            handleGoToLine={handleGoToLine}
            navigation={{
              current: currentErrorIndex,
              total: errors?.length || 0,
              handleSwitch: handleSwitch,
            }}
          />
        </Grid>
      )}

      {hasTwoPanels ? (
        <Grid item xs={12}>
          <WbDockableGroup
            direction="horizontal"
            isSidebarCollapsed={isPrimaryCollapsed}
            onSidebarCollapsedChange={setIsPrimaryCollapsed}
          >
            {(policy.engine === Engine.Cue && (
              <WbDockablePanel
                maxSize={50}
                defaultSize={50}
                minSize={20}
                title="Cue Script"
                headerBorderBottom
                cardWrapper={false}
                headerActions={
                  <EditorInfo
                    editable={false}
                    clipboardText={policy.cueSpecific.cueScript}
                  />
                }
              >
                <Editor
                  id={`policy-${policy.groupId}`}
                  content={policy.cueSpecific.cueScript}
                  editable={false}
                  cardWrapper={false}
                  highlightLines={currentError?.policyErrorRows}
                  parser="go"
                />
              </WbDockablePanel>
            )) ||
              (policy.engine === Engine.GovernanceAgent && (
                <WbDockablePanel
                  maxSize={50}
                  defaultSize={50}
                  minSize={20}
                  headerBorderBottom
                  title="Policy Content"
                  headerActions={
                    <EditorInfo
                      editable={false}
                      clipboardText={policy.governanceAgentSpec.prompt}
                    />
                  }
                >
                  <Editor
                    cardWrapper={false}
                    id={`policy-${policy.groupId}`}
                    content={policy.governanceAgentSpec.prompt}
                    editable={false}
                    highlightLines={currentError?.policyErrorRows}
                    parser="go"
                  />
                </WbDockablePanel>
              ))}

            <WbDockableResizeHandle direction="horizontal" spacing />

            <WbDockablePanel
              collapsible
              title="Descriptor"
              headerBorderBottom
              headerActions={
                <EditorInfo editable={false} clipboardText={test.content} />
              }
            >
              <Editor
                cardWrapper={false}
                id={`descriptor-${policy.groupId}`}
                editable={false}
                content={test.content}
                highlightLines={currentError?.descriptorErrorRows}
              />
            </WbDockablePanel>
          </WbDockableGroup>
        </Grid>
      ) : (
        <>
          {(policy.engine === Engine.Cue && (
            <Grid item xs={12} lg={6}>
              <Editor
                id={`policy-${policy.groupId}`}
                name="Cue Script"
                content={policy.cueSpecific.cueScript}
                editable={false}
                highlightLines={currentError?.policyErrorRows}
                parser="go"
              />
            </Grid>
          )) ||
            (policy.engine === Engine.GovernanceAgent && (
              <Grid item xs={12} lg={6}>
                <Editor
                  id={`policy-${policy.groupId}`}
                  name="Policy Content"
                  content={policy.governanceAgentSpec.prompt}
                  editable={false}
                  highlightLines={currentError?.policyErrorRows}
                  parser="go"
                />
              </Grid>
            ))}
          {(test.content && (
            <Grid item xs={12} lg={policy.engine === Engine.Cue ? 6 : 12}>
              <Editor
                id={`descriptor-${policy.groupId}`}
                name="Descriptor"
                editable={false}
                content={test.content}
                highlightLines={currentError?.descriptorErrorRows}
              />
            </Grid>
          )) || (
            <Grid
              item
              xs={12}
              lg={policy.engine === Engine.GovernanceAgent ? 6 : 12}
            >
              <Editor
                id={`descriptor-${policy.groupId}`}
                name="Descriptor"
                editable={false}
                content={test.content}
                highlightLines={currentError?.descriptorErrorRows}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};
